<script setup lang="ts">
import Curtains from '@/shared/components/core/Curtains/Curtains.vue';
import { useLoaderAtStartStore } from '@/shared/components/loaders/LoaderAtStartApp/store.ts';
import IconAnimatedLogo from '@/shared/components/icons/IconAnimatedLogo.vue';
import { useAudioAutoEnable } from '@/shared/composables/useAudioAutoEnable.ts';
import { useMainStore } from '@/core/store/main.ts';
import { MaybeNull } from '@/shared/types';
import anime from 'animejs';
import { storeToRefs } from 'pinia';
import {
  computed,
  nextTick,
  onMounted,
  onUnmounted,
  ref,
  Ref,
  watch,
} from 'vue';
import FadeTransition from '@/shared/components/transitions/FadeTransition.vue';
import HelloScreen from './HelloScreen.vue';
import { useI18n } from 'vue-i18n';
import { createAnimation, Animation as IonicAnimation } from '@ionic/vue';
import { getSafeRef } from '@/shared/utils/template-refs.ts';

const { t } = useI18n();

const emit = defineEmits(['animated-complete']);

const ANIMATION_DURATION = 6000;

const tagline$: Ref<MaybeNull<HTMLElement>> = ref(null);
const curtains$: Ref<MaybeNull<typeof Curtains>> = ref(null);
const container$: Ref<MaybeNull<HTMLElement>> = ref(null);

const curtainsVisible = ref(false);

const disabledLoader = ref(true);

const playLogoAnimation = ref(false);

let animationTimeline: MaybeNull<IonicAnimation>;

const {
  actionPanelAnimeCallback,
  desktopSlideContainerWidth,
  desktopSlideContainerHeight,
} = storeToRefs(useMainStore());

const { visible, loaderIsRequired } = storeToRefs(useLoaderAtStartStore());

const helloScreenVisible = ref(false);

const internetAvailable = ref(typeof window !== 'undefined' ? window.navigator.onLine : true);

const createAndPlayAnimation = () => {
  animationTimeline = createAnimation()
    .duration(ANIMATION_DURATION)
    .onFinish(() => {
      emit('animated-complete');
      showHelloScreen();
      playLogoAnimation.value = false;
    });

  // tagline fade in + move up
  const taglineIn = createAnimation()
    .addElement(getSafeRef(tagline$))
    .duration(1500)
    .easing('cubic-bezier(0.25, 0.46, 0.45, 0.94)') // easeOutQuad
    .fromTo('color', 'rgba(255,255,255,0)', 'rgba(255,255,255,0.7)')
    .fromTo('transform', 'translateY(10px)', 'translateY(0px)')
    .delay(1600);

  // tagline fade out + move up more
  const taglineOut = createAnimation()
    .addElement(getSafeRef(tagline$))
    .duration(1000)
    .easing('cubic-bezier(0.25, 0.46, 0.45, 0.94)') // easeOutQuad
    .fromTo('color', 'rgba(255,255,255,0.7)', 'rgba(255,255,255,0)')
    .fromTo('transform', 'translateY(0px)', 'translateY(-30px)')
    .fill('forwards')
    .delay(5000);

  animationTimeline.addAnimation([
    taglineIn,
    taglineOut,
  ]);

  animationTimeline.play();

  playLogoAnimation.value = true;
};

const removeLoader = () => {
  actionPanelAnimeCallback.value();

  curtains$.value?.openCurtains({
  }).then(() => {
    curtainsVisible.value = false;
    disabledLoader.value = true;

    visible.value = false;
  });
};

const showHelloScreen = () => {
  helloScreenVisible.value = true;

  setTimeout(() => {
    if (!internetAvailable.value) {
      // якщо інтернету нема, то ми залишаємо статичний екран
      // коли з'явиться інтерент, додаток має перезавантажитись
      return;
    }

    if (!loaderIsRequired.value) {
      helloScreenVisible.value = false;
      removeLoader();
    } else {
      watch(() => loaderIsRequired.value, (value) => {
        if (!value) {
          helloScreenVisible.value = false;
          removeLoader();
        }
      });
    }
  }, 6000);
};

watch(() => visible.value, (newValue) => {
  if (newValue) {
    nextTick(() => {
      curtainsVisible.value = true;
      disabledLoader.value = false;

      anime({
        targets: container$.value,
        opacity: [0, 1],
        duration: 500,
        easing: 'easeOutQuad',
      });

      createAndPlayAnimation();

      curtainsVisible.value = true;
    });
  } else {
    curtainsVisible.value = false;
    disabledLoader.value = true;
  }
});

const handleOnline = () => {
  internetAvailable.value = true;
  animationTimeline?.play();
  helloScreenVisible.value = false;
};

const handleOffline = () => {
  internetAvailable.value = false;
  animationTimeline?.pause();
};

onMounted(() => {
  if (visible.value) {
    // todo: refactor
    disabledLoader.value = false;

    createAndPlayAnimation();

    curtainsVisible.value = true;
  }

  window.addEventListener('online', handleOnline);
  window.addEventListener('offline', handleOffline);
});

onUnmounted(() => {
  window.removeEventListener('online', handleOnline);
  window.removeEventListener('offline', handleOffline);
});

const { enableAudio } = useAudioAutoEnable();

const desktopSlideContainerWidthPx = computed(() => desktopSlideContainerWidth.value + 'px');

const desktopSlideContainerHeightPx = computed(() => desktopSlideContainerHeight.value + 'px');

</script>

<template>
  <div
    ref="container$"
    :class="{
      'loader-at-start-app-conteiner': true,
      'disabled-loader': disabledLoader,
    }"
    @click="enableAudio"
  >
    <div class="content">
      <IconAnimatedLogo
        :play-animation="playLogoAnimation"
        loop
      />
      <div
        ref="tagline$"
        class="tagline"
      >
        {{ t('app_launch_loader.text_1') }}<br>
        {{ t('app_launch_loader.text_2') }}
      </div>
    </div>

    <FadeTransition
      :visible="helloScreenVisible"
      time="0.5s"
    >
      <div class="hello-screen-wrapper">
        <HelloScreen />
      </div>
    </FadeTransition>

    <Curtains
      v-if="curtainsVisible"
      ref="curtains$"
      class="curtains"
    />
  </div>
</template>

<style scoped>
.loader-at-start-app-conteiner {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  pointer-events: none;
}

@media all and (min-width: 601px) {
  .loader-at-start-app-conteiner {
    border-radius: var(--desktop-corner-radius);
    overflow: hidden;
  }
}

.disabled-loader {
  display: none;
}

.content {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  z-index: 2;
}

.tagline {
  text-align: center;
  color: rgba(255,255,255,0.7);
  text-align: center;
  font-family: var(--fontFamily);
  font-size: 18px;
  font-style: normal;
  font-weight: 400;
  line-height: 134%;

  margin-top: -57px;
}

.curtains {
  position: absolute;
  top: 0;
  left: 0;
  z-index: 1;
}

.hello-screen-wrapper {
  z-index: 550;
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: v-bind(desktopSlideContainerWidthPx);
  height: v-bind(desktopSlideContainerHeightPx);
  overflow: hidden;
  pointer-events: none;

  @media all and (min-width: 601px) {
    border-radius: var(--desktop-corner-radius);
  }
}

</style>
